import React from "react";

import {
  ONBOARDING_INTERESTED_INDUSTRY_META,
  ONBOARDING_INVESTING_YEAR_META,
} from "@lattice/core-lib/types/onboarding/onboarding.types";
import { E_UsageFeature } from "@lattice/core-lib/types/usage";
import { getUsageFeatureProjection } from "@lattice/shared-frontend/usage";

import { Surface } from "@lattice/frontend/components/kit/Surface";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";
import { resolveUserDisplayName } from "@lattice/shared-frontend/shell";

import { SettingsPanelError, SettingsPanelLoading } from "./SettingsPanelState";

export type T_SettingsRootPanelViewProps = {
  alertUsage: string;
  brokerageSummary: string;
  displayName: string;
  planLabel: string;
  prismRemaining: string;
  profileSummary: string;
};

export function SettingsRootPanelView({
  alertUsage,
  brokerageSummary,
  displayName,
  planLabel,
  prismRemaining,
  profileSummary,
}: T_SettingsRootPanelViewProps) {
  const initials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "L";

  return (
    <Surface padding="none" tone="subtle">
      <div className="shell-settings-summary__identity">
        <span aria-hidden className="shell-settings-summary__avatar">
          {initials}
        </span>
        <span className="shell-settings-summary__name">{displayName}</span>
        <span className="shell-settings-summary__plan">{planLabel}</span>
      </div>
      <dl className="shell-settings-summary__stats">
        <div>
          <dt>Prism messages left</dt>
          <dd>{prismRemaining}</dd>
        </div>
        <div>
          <dt>Price alerts</dt>
          <dd>{alertUsage}</dd>
        </div>
      </dl>
      <dl className="shell-settings-summary__details">
        <div>
          <dt>Investor profile</dt>
          <dd>{profileSummary}</dd>
        </div>
        <div>
          <dt>Brokerages</dt>
          <dd>{brokerageSummary}</dd>
        </div>
      </dl>
    </Surface>
  );
}

export function SettingsRootPanel() {
  const currentUserQuery = trpcApi.users.get_current_user.useQuery({});
  const profileQuery = trpcApi.users.get_investor_profile.useQuery();
  const registrationQuery = trpcApi.snaptrade.getRegistrationStatus.useQuery();
  const usageQuery = trpcApi.usage.get_usage_stats.useQuery();

  if (
    currentUserQuery.isPending ||
    profileQuery.isPending ||
    registrationQuery.isPending ||
    usageQuery.isPending
  ) {
    return <SettingsPanelLoading label="Loading account summary" />;
  }
  if (
    currentUserQuery.isError ||
    profileQuery.isError ||
    registrationQuery.isError ||
    usageQuery.isError ||
    !currentUserQuery.data ||
    !profileQuery.data ||
    !usageQuery.data
  ) {
    return (
      <SettingsPanelError message="Account settings could not be loaded." />
    );
  }

  const user = currentUserQuery.data;
  const profile = profileQuery.data.data;
  // Same shared rule as mobile (RAI-1099) so identity surfaces agree.
  // Web's session pipeline does not currently expose the Logto name/email
  // claims to this client component, so those inputs are null here; wiring
  // them through is the follow-up that makes web match mobile for accounts
  // with empty DB name fields.
  const displayName = resolveUserDisplayName({
    dbFullName: [user.first_name, user.last_name].filter(Boolean).join(" "),
    email: null,
    identityName: null,
  });
  const subscriptionTier = user.Subscription?.tier;
  const planLabel =
    user.is_premium &&
    (subscriptionTier === "PRO" || subscriptionTier === "MAX")
      ? `${subscriptionTier === "MAX" ? "Max" : "Pro"} plan`
      : "Free plan";
  const investingYears = profile.investing_years
    ? ONBOARDING_INVESTING_YEAR_META[profile.investing_years].text
    : null;
  const industries = profile.interested_industries
    .slice(0, 2)
    .map((industry) => ONBOARDING_INTERESTED_INDUSTRY_META[industry].text)
    .join(", ");
  const profileSummary =
    [investingYears, industries].filter(Boolean).join(" · ") ||
    "Complete your profile";
  const accountCount = registrationQuery.data?.accountCount ?? 0;
  const prismUsage = getUsageFeatureProjection(
    usageQuery.data.features,
    E_UsageFeature.PRISM_QUERIES,
  );

  return (
    <SettingsRootPanelView
      alertUsage={String(user.usage.alert)}
      brokerageSummary={
        accountCount === 0
          ? "No brokerage connected"
          : `${accountCount} ${accountCount === 1 ? "account" : "accounts"} connected`
      }
      displayName={displayName}
      planLabel={planLabel}
      prismRemaining={
        prismUsage?.remaining === null
          ? "Included"
          : String(prismUsage?.remaining ?? "—")
      }
      profileSummary={profileSummary}
    />
  );
}
